import type { ReactNode } from "react";
import { Link } from "react-router-dom";

/**
 * Card uniforme para un servicio activable de la alcaldía.
 *
 * Vive en /tenants/:id/servicios como hub central. Cada servicio
 * (turnos, GD actos, GD correspondencia, RAG, AI draft, firma)
 * comparte el mismo patrón visual: descripción + toggle + link a
 * configuración detallada (si aplica) + capacidades.
 *
 * El toggle vive aquí Y en la página de detalle de cada servicio
 * (decisión de producto — el super-admin puede prender desde el
 * hub para descubrir capacidades, o desde la página de config si
 * ya estaba ajustando otra cosa).
 */
export function ServiceToggleCard({
  eyebrow,
  title,
  description,
  enabled,
  busy,
  onToggle,
  configurarPath,
  urls,
  capacidades,
  status,
  disabled,
  disabledReason,
}: {
  eyebrow: string;
  title: string;
  description: ReactNode;
  enabled: boolean;
  busy: boolean;
  onToggle: () => void;
  /** Ruta absoluta a la página de configuración detallada del servicio. */
  configurarPath?: string;
  /** URLs públicas a mostrar cuando el servicio está activo. */
  urls?: { label: string; url: string }[];
  /** Bullet list de lo que incluye el servicio. */
  capacidades?: string[];
  /** Override del texto del estado (ej. "Sin certificado cargado"). */
  status?: { label: string; tone: "ok" | "warn" | "err" | "muted" };
  /** El toggle no se puede usar (ej. faltan credenciales globales). */
  disabled?: boolean;
  /** Razón visible cuando `disabled=true`. */
  disabledReason?: ReactNode;
}) {
  const toneColor =
    status?.tone === "ok"
      ? "var(--ok)"
      : status?.tone === "warn"
        ? "var(--warn)"
        : status?.tone === "err"
          ? "var(--err)"
          : "var(--ink-3)";

  return (
    <div className="card" style={{ marginTop: 18 }}>
      <div className="card-inner">
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="eyebrow">{eyebrow}</div>
            <h2
              style={{
                fontSize: 20,
                marginTop: 6,
                marginBottom: 6,
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              {title}
              <span
                style={{
                  fontSize: 11,
                  fontFamily: "var(--font-mono)",
                  color: toneColor,
                  padding: "2px 8px",
                  border: `1px solid ${toneColor}`,
                  borderRadius: 999,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                }}
              >
                {status?.label ?? (enabled ? "Activo" : "Inactivo")}
              </span>
            </h2>
            <p
              style={{
                margin: "0 0 14px",
                color: "var(--ink-2)",
                fontSize: 14,
                lineHeight: 1.55,
                maxWidth: 640,
              }}
            >
              {description}
            </p>
          </div>
        </div>

        {disabled && disabledReason && (
          <div className="alert warn" style={{ marginTop: 4, marginBottom: 12 }}>
            {disabledReason}
          </div>
        )}

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 4,
            cursor: disabled || busy ? "not-allowed" : "pointer",
            padding: "10px 12px",
            background: enabled ? "var(--bone-warm)" : "transparent",
            border: "1px solid var(--hairline)",
            borderRadius: 8,
            opacity: disabled ? 0.55 : 1,
          }}
        >
          <input
            type="checkbox"
            checked={enabled}
            disabled={busy || disabled}
            onChange={onToggle}
            style={{ width: 18, height: 18 }}
          />
          <span style={{ fontSize: 14, color: "var(--ink)" }}>
            {enabled ? `${title} — activado` : `${title} — desactivado`}
          </span>
        </label>

        {enabled && urls && urls.length > 0 && (
          <div
            style={{
              marginTop: 12,
              fontSize: 13,
              color: "var(--ink-3)",
              lineHeight: 1.6,
            }}
          >
            {urls.map((u) => (
              <div key={u.url}>
                {u.label}: <code>{u.url}</code>
              </div>
            ))}
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginTop: 14,
            flexWrap: "wrap",
          }}
        >
          {capacidades && capacidades.length > 0 ? (
            <details style={{ flex: 1, minWidth: 240 }}>
              <summary
                style={{
                  cursor: "pointer",
                  fontSize: 12,
                  color: "var(--ink-3)",
                  fontFamily: "var(--font-mono)",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                Capacidades incluidas
              </summary>
              <ul
                style={{
                  marginTop: 10,
                  paddingLeft: 20,
                  fontSize: 13.5,
                  lineHeight: 1.7,
                  color: "var(--ink-2)",
                }}
              >
                {capacidades.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </details>
          ) : (
            <span />
          )}

          {configurarPath && (
            <Link
              to={configurarPath}
              className="btn-outline btn-sm"
              style={{ flexShrink: 0 }}
            >
              Configurar →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
